import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const MOBILE_QUERY = '(max-width: 768px)';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_QUERY);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

const navLinkStyle = { color: 'var(--text-dim)', textDecoration: 'none', fontSize: 14 };
const signInStyle = { color: 'var(--accent)', fontWeight: 600, textDecoration: 'none', fontSize: 14 };

function FeaturesLink({ onClick, style }) {
  // Anchor link: works on landing as in-page scroll, navigates to landing + scrolls from other pages.
  return <a href="/#features" onClick={onClick} style={style}>Features</a>;
}

function PricingAnchorLink({ onClick, style }) {
  return <Link to="/pricing" onClick={onClick} style={style}>Pricing</Link>;
}

export default function PublicHeader() {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // ESC closes drawer
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Lock body scroll while drawer open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const brand = (
    <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: 'var(--text)' }}>
      <img src="/trainforge-logo.svg" alt="" width={28} height={28} />
      <span style={{ fontWeight: 700, fontSize: 16 }}>TrainForge</span>
    </Link>
  );

  return (
    <header
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '14px 20px',
        // Clear the iOS status bar / notch in the Capacitor shell + PWA.
        paddingTop: 'calc(14px + env(safe-area-inset-top))',
        borderBottom: '1px solid var(--border)',
        position: 'sticky',
        top: 0,
        background: 'var(--bg)',
        zIndex: 50,
      }}
    >
      {brand}

      {!isMobile && (
        <nav style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
          <FeaturesLink style={navLinkStyle} />
          <PricingAnchorLink style={navLinkStyle} />
          <Link to="/about" style={navLinkStyle}>About</Link>
          <Link to="/contact" style={navLinkStyle}>Contact</Link>
          <Link to="/login" style={signInStyle}>Sign in</Link>
        </nav>
      )}

      {isMobile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Link to="/login" style={signInStyle}>Sign in</Link>
          <button
            type="button"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="public-header-drawer"
            onClick={() => setOpen((v) => !v)}
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 8,
              width: 40,
              height: 40,
              minHeight: 40,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 4,
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <span style={{ display: 'block', width: 18, height: 2, background: 'var(--text)' }} />
            <span style={{ display: 'block', width: 18, height: 2, background: 'var(--text)' }} />
            <span style={{ display: 'block', width: 18, height: 2, background: 'var(--text)' }} />
          </button>
        </div>
      )}

      {isMobile && open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              top: 64,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 49,
            }}
          />
          <nav
            id="public-header-drawer"
            style={{
              position: 'fixed',
              top: 64,
              left: 0,
              right: 0,
              background: 'var(--bg-elev)',
              borderBottom: '1px solid var(--border)',
              padding: '8px 20px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              zIndex: 50,
            }}
          >
            <FeaturesLink
              onClick={() => setOpen(false)}
              style={{ ...navLinkStyle, padding: '14px 4px', fontSize: 16, borderBottom: '1px solid var(--border)' }}
            />
            <PricingAnchorLink
              onClick={() => setOpen(false)}
              style={{ ...navLinkStyle, padding: '14px 4px', fontSize: 16, borderBottom: '1px solid var(--border)' }}
            />
            <Link
              to="/about"
              onClick={() => setOpen(false)}
              style={{ ...navLinkStyle, padding: '14px 4px', fontSize: 16, borderBottom: '1px solid var(--border)' }}
            >
              About
            </Link>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              style={{ ...navLinkStyle, padding: '14px 4px', fontSize: 16 }}
            >
              Contact
            </Link>
          </nav>
        </>
      )}
    </header>
  );
}
